import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { ChevronDown, Search, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface DropdownOption {
  value: string | number
  label: string
  icon?: React.ReactNode
}

interface DropdownProps {
  options: DropdownOption[]
  value?: string | number
  onChange?: (value: string | number) => void
  placeholder?: string
  /**
   * Styling for the control itself — the thing that looks like a button.
   *
   * This used to land on the outer wrapper instead, which is a transparent, square-cornered box
   * around a rounded pill. Anything visual passed here was therefore drawn in the wrong place:
   * a shadow became a grey rectangle behind the control, and a radius or a font did nothing at
   * all. It was invisible on a dark page, which is why it survived so long.
   */
  className?: string
  /** Layout for the wrapper — width, positioning. Rarely needed; `fullWidth` covers most of it. */
  wrapperClassName?: string
  variant?: 'primary' | 'dark' | 'gold'
  size?: 'sm' | 'md' | 'lg'
  fullWidth?: boolean
  disabled?: boolean
  /** Show a type-to-filter search box at the top of the panel (useful for long lists). */
  searchable?: boolean
  searchPlaceholder?: string
}

interface Ripple { id: number; x: number; y: number }

// MUI-matching elevation shadows (same system as MUI contained button)
const muiShadowRest  = '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)'
const muiShadowHover = '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)'
const panelShadow    = '0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)'

export const Dropdown: React.FC<DropdownProps> = ({
  options,
  value,
  onChange,
  placeholder,
  className = '',
  wrapperClassName = '',
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  disabled = false,
  searchable = false,
  searchPlaceholder,
}) => {
  const { t } = useTranslation('ui')
  const resolvedPlaceholder = placeholder ?? t('dropdown.placeholder')
  const [isOpen, setIsOpen] = useState(false)
  const [hovered, setHovered] = useState(false)
  const [ripples, setRipples] = useState<Ripple[]>([])
  const [openUpward, setOpenUpward] = useState(false)
  const [query, setQuery] = useState('')
  const dropdownRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  const selectedOption = options.find((opt) => opt.value === value)

  const filteredOptions = useMemo(() => {
    if (!searchable) return options
    const q = query.trim().toLowerCase()
    if (!q) return options
    return options.filter((o) => o.label.toLowerCase().includes(q))
  }, [options, query, searchable])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Reset the query and focus the search box each time the panel opens.
  useEffect(() => {
    if (isOpen && searchable) {
      setQuery('')
      const id = window.setTimeout(() => searchRef.current?.focus(), 0)
      return () => window.clearTimeout(id)
    }
  }, [isOpen, searchable])

  const triggerVariant = {
    // hover:bg-pcg-goldDark rather than gold/90: an alpha hover lets the page show through a
    // filled control, which reads as the button fading rather than darkening under the cursor.
    primary: 'bg-pcg-gold hover:bg-pcg-goldDark text-black',
    dark:    'bg-pcg-borderStrong hover:bg-pcg-borderMuted text-white border border-pcg-gold/20',
    gold:    'bg-pcg-gold hover:bg-pcg-goldDark text-black',
  }

  // Sized off custom-button.tsx, whose gold button is 44px tall at 15px. sm stays shorter for
  // toolbars, but the type scale and side padding now line up with it.
  const sizeClasses = {
    sm: 'h-[40px] px-5 text-[14px]',
    md: 'h-[44px] px-6 text-[15px]',
    lg: 'h-[48px] px-6 text-[15px]',
  }

  const handleSelect = (option: DropdownOption) => {
    onChange?.(option.value)
    setIsOpen(false)
  }

  const handleTriggerClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) return
    const rect = e.currentTarget.getBoundingClientRect()
    const id = Date.now()
    setRipples(prev => [...prev, { id, x: e.clientX - rect.left, y: e.clientY - rect.top }])
    setTimeout(() => setRipples(prev => prev.filter(r => r.id !== id)), 600)
    // Flip the panel upward when there isn't enough room below the trigger.
    const panelMaxHeight = 280
    const spaceBelow = window.innerHeight - rect.bottom
    const spaceAbove = rect.top
    setOpenUpward(spaceBelow < panelMaxHeight && spaceAbove > spaceBelow)
    setIsOpen(prev => !prev)
  }, [disabled])

  return (
    <div
      ref={dropdownRef}
      className={`relative ${fullWidth ? 'w-full' : 'inline-block'} ${wrapperClassName}`}
    >
      {/* Trigger — matches MUI contained button visually */}
      <button
        type="button"
        onClick={handleTriggerClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          boxShadow: hovered ? muiShadowHover : muiShadowRest,
          transition: 'box-shadow 250ms cubic-bezier(0.4,0,0.2,1), background-color 250ms cubic-bezier(0.4,0,0.2,1)',
          letterSpacing: variant === 'dark' ? 'normal' : '0.01em',
        }}
        className={`
          relative overflow-hidden inline-flex items-center justify-between ${fullWidth ? 'w-full' : 'w-auto'}
          ${variant === 'dark' ? 'font-proxima-regular normal-case tracking-normal' : 'font-proxima-bold normal-case'}
          outline-none rounded-[20px] active:scale-[0.96]
          ${disabled ? 'opacity-40 cursor-not-allowed pointer-events-none' : triggerVariant[variant]}
          ${sizeClasses[size]}
          ${className}
        `}
      >
        {/* Ripple */}
        {ripples.map(r => (
          <span key={r.id} className="btn-ripple" style={{ left: r.x, top: r.y, background: 'rgba(0,0,0,0.12)' }} />
        ))}

        <span className="w-4 shrink-0" />
        <span className="flex-1 text-center truncate px-2">
          {selectedOption ? selectedOption.label : (
            <span className="opacity-70 normal-case tracking-normal font-medium">
              {resolvedPlaceholder}
            </span>
          )}
        </span>
        <ChevronDown
          className={`ml-2 shrink-0 transition-transform duration-200 pointer-events-none ${isOpen ? 'rotate-180' : ''}`}
          size={18}
        />
      </button>

      {/* Panel */}
      {isOpen && (
        <div
          style={{ boxShadow: panelShadow }}
          className={`
            absolute z-[200] w-full flex flex-col overflow-hidden
            bg-pcg-raised border border-white/10 rounded-[12px]
            ${openUpward ? 'bottom-full mb-1.5' : 'top-full mt-1.5'}
          `}
        >
          {searchable && (
            <div className="relative p-2 border-b border-white/10 shrink-0">
              <Search
                size={14}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30 pointer-events-none"
              />
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') { e.preventDefault(); setIsOpen(false) }
                }}
                placeholder={searchPlaceholder ?? t('dropdown.searchPlaceholder')}
                className="w-full bg-pcg-card border border-pcg-gold/15 rounded text-white font-proxima-regular text-[13px] pl-7 pr-7 py-1.5 focus:outline-none focus:border-pcg-gold/40"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => { setQuery(''); searchRef.current?.focus() }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60"
                >
                  <X size={13} />
                </button>
              )}
            </div>
          )}
          <div className="overflow-auto max-h-[280px] custom-scrollbar">
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-3 text-center text-white/30 font-proxima-regular text-[13px]">
                {t('dropdown.noMatches')}
              </div>
            ) : (
              filteredOptions.map((option) => {
                const isActive = option.value === value
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleSelect(option)}
                    style={{ letterSpacing: '0.01em' }}
                    className={`
                      flex items-center gap-3 w-full px-4 py-[10px] text-left
                      font-proxima-regular text-[14px] transition-colors duration-150
                      ${isActive
                        ? 'bg-pcg-gold/15 text-pcg-gold font-proxima-bold'
                        : 'text-white/80 hover:bg-white/5 hover:text-white'}
                    `}
                  >
                    {option.icon}
                    {option.label}
                  </button>
                )
              })
            )}
          </div>
        </div>
      )}
    </div>
  )
}
